import { useEffect, useRef, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  webChatPanelUsersPath
} from "../constants/chatApiPaths";
import { DeliveredToAll, getChat, pushMoreChat, SeenByAll, syncChat, syncChatMessages } from "../localDb/chat";
import Api from "../service/http";
import { useSocket } from "../service/SocketProvider";
import { hasActiveAccess } from "../utils/subscription";
import Details from "./chat/Details";
import Messages from "./chat/Messages";

const Chat = () => {
  const chat_id = useParams().id;
  const chatId = useRef();
  const { socketSend, connectionVersion } = useSocket();
  const { theme } = useSelector((state) => state.theme);

  const user = JSON.parse(localStorage.getItem("user"));
  const subscribed = hasActiveAccess(user);

  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(false);

  const getChatFromServer = async (isScroll = true) => {
    const chatData = getChat(chatId?.current);
    setChat(chatData);

    if (isScroll) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      scrollToBottom(false);
    }

    try {
      const response = await Api.get("/web/chat/" + chatId?.current, {
        params: {
          min_id: chatData?.first_message_id ?? null,
        },
      });
      if (response.data?.data) {
        if (chatId?.current == response.data?.data?.id) {
          let nextChat = response.data.data;
          try {
            const membersRes = await Api.get(
              `${webChatPanelUsersPath(nextChat.id)}?page=1&per_page=-1`
            );
            if (Array.isArray(membersRes.data?.data)) {
              nextChat = { ...nextChat, users: membersRes.data.data };
            }
          } catch (e) {
            console.error("Error fetching chat panel details:", e);
          }
          syncChat(nextChat);
          setChat(nextChat);
        }
        // Fetching the chat marks it read server-side; no socket round trip needed.
      }
    } catch (error) {
      console.error("Error fetching chat from server:", error);
    }
  }

  useEffect(() => {
    if (!subscribed) return;
    if (chat_id) {
      chatId.current = chat_id;
      getChatFromServer();
    }
  }, [chat_id, subscribed]);

  // After a reconnect, re-fetch the open chat: anything pushed while we were offline was missed.
  const seenConnectionVersion = useRef(connectionVersion);
  useEffect(() => {
    if (connectionVersion === seenConnectionVersion.current) return;
    seenConnectionVersion.current = connectionVersion;
    if (subscribed && chatId.current) {
      getChatFromServer(false);
    }
  }, [connectionVersion]);

  // Only count messages as read while the user can actually see them.
  const markReadIfVisible = (id) => {
    if (document.visibilityState === "visible") {
      socketSend({ type: "ReadByAll", chat_id: id, user_id: user?.id });
    }
  };
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && chatId.current) {
        socketSend({ type: "ReadByAll", chat_id: chatId.current, user_id: user?.id });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [socketSend]);

  const loadMoreFromServer = async () => {
    if (loading) return;
    setLoading(true);
    const prevFirstMessageId = chat?.first_message_id;

    let response = null;
    try {
      response = await Api.get("/web/chat/" + chat?.id + "/more", {
        params: {
          min_id: prevFirstMessageId ?? null,
        },
      });
    } catch (error) {
      console.error("Error loading older messages:", error);
    }

    if (response?.status === 200 && response.data?.data) {
      const payload = response.data.data;
      setChat(
        pushMoreChat({
          ...payload,
          users: chat?.users,
        })
      );

      setTimeout(() => {
        const el = document.getElementById(`message-${prevFirstMessageId}`);
        if (el) {
          el.scrollIntoView({ behavior: "instant", block: "start" });
          const chatElement = document.querySelector(".chat-list");
          if (chatElement) {
            chatElement.scrollTop = el.offsetTop - 150;
          }
        }
      }, 1);
    }
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const checkScrollPosition = () => {
    const chatElement = document.querySelector(".chat-list");
    if (chatElement) {
      // return bottom/top/middle
      const scrollTop = chatElement.scrollTop;
      const scrollHeight = chatElement.scrollHeight;
      const clientHeight = chatElement.clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // 5px tolerance
      const isAtTop = scrollTop <= 5; // 5px tolerance  

      if (isAtBottom) {
        return "bottom";
      }
      if (isAtTop) {
        return "top";
      }
      return "middle";
    }
  }

  const scrollToBottom = (behavior = true) => { 
    const chatElement = document.querySelector(".chat-list");
    chatElement?.scrollTo({
      top: chatElement.scrollHeight,
      behavior: behavior ? "smooth" : "auto"
    });
  }

  const { socket, subscribe, unsubscribe } = useSocket();

  const [typingUser, setTypingUser] = useState([]);
  const controlTyping = (chat_id, typeUser, typing) => {
    if (chat_id != chatId?.current) return;
    if (typeUser?.id == user?.id) return;

    const now = new Date().toISOString();

    if (typing) {
      setTypingUser((prevTypingUsers) => {
        const existingIndex = prevTypingUsers.findIndex(
          (item) => item?.id === typeUser?.id
        );

        if (existingIndex !== -1) {
          // Update timestamp
          const updatedUsers = [...prevTypingUsers];
          updatedUsers[existingIndex] = {
            ...updatedUsers[existingIndex],
            lastTypedAt: now,
          };
          return updatedUsers;
        } else {
          // Add new user
          return [...prevTypingUsers, { ...typeUser, lastTypedAt: now }];
        }
      });

      if (checkScrollPosition() === "bottom") {
        scrollToBottom();
      }
    } else {
      setTypingUser((prevTypingUsers) =>
        prevTypingUsers?.filter((item) => item?.id !== typeUser?.id)
      );
    }
  };
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUser((prevTypingUsers) => {
        const now = Date.now();
        return prevTypingUsers.filter(user =>
          user.lastTypedAt && (new Date(user.lastTypedAt).getTime() > now - 5000)
        );
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMessage = (data) => {
      switch (data.type) {
        case "WebMeesageTyping":
          controlTyping(data.chat_id, data?.user, data.typing);
          break;
        case "WebFetchMessage":
          if (data?.message?.chat_id == chatId?.current) {
            syncChatMessages(data?.message?.chat_id, data?.message);
            setChat(getChat(chatId?.current));
            if (data?.isScroll) {
              scrollToBottom(data?.isScroll);
            }
            if (data?.message?.sender?.id != user?.id && data?.message?.user_id != user?.id) {
              markReadIfVisible(data?.message?.chat_id);
            }
          }
          break;
        case "WebFetchChat":
          if ((data?.chat_id ?? data?.message?.chat_id) == chatId?.current) {
            getChatFromServer(data?.isScroll);
          }
          break;
        case "DeliveredToAll":
          DeliveredToAll(data?.chats, user?.id);
          if (data?.chats?.some((chat) => chat?.id == chatId?.current)) {
            setChat(getChat(chatId?.current));
          }
          break;
        case "ReadByAll":
          SeenByAll(data?.chat, user?.id);
          if (data?.chat?.id == chatId?.current) {
            setChat(getChat(chatId?.current));
          }
          break;
      }
    };

    if (socket) subscribe(handleMessage);
    return () => unsubscribe(handleMessage);
  }, [socket, subscribe, unsubscribe]);

  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {chat ? (
        <div className="flex h-full w-full relative">
          <Messages
            chat={chat}
            setChat={setChat}
            messages={chat?.messages}
            typingUser={typingUser}
            scrollToBottom={scrollToBottom}
            loadMoreFromServer={loadMoreFromServer}
            moreLoading={loading}
            showDetails={showDetails}
            setShowDetails={setShowDetails}
          />
          <Details chat={chat} setChat={setChat} showDetails={showDetails} setShowDetails={setShowDetails} />
        </div>
      ) : (
        <span className="flex h-full w-full items-center justify-center">
          <FaSpinner className={`animate-spin text-3xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'} m-auto`} />
        </span>
      )}
    </div>
  );
};

export default Chat;
